import Thumbtack from "@/assets/icons/thumbtack.svg?react";
import { StateMessage } from "@/ds";
import { ObrasView } from "./components/obras-view";
import { OverviewView } from "./components/overview-view";
import styles from "./home.module.scss";
import type { HomeViewModel } from "./types-home";

/**
 * Tela Inicial do Hub (US-1614): porta de entrada do sistema, com a Visão Geral
 * do portfólio e as Obras em Andamento sobre o painel escuro do protótipo.
 *
 * Uma única consulta alimenta as duas visões, de modo que alternar entre elas é
 * apenas troca de apresentação. A seção de notificações do protótipo não faz
 * parte desta entrega (AC9) e os itens de menu lateral seguem os já existentes
 * (AC8), por isso a tela não os declara.
 */
export function HomeView({
  visoes,
  visaoAtiva,
  visaoAtivaLabel,
  isVisaoGeral,
  papelPainel,
  isNarrow,
  status,
  errorMessage,
  overview,
  obrasEmAndamento,
  selecionarVisao,
  tentarNovamente,
}: HomeViewModel) {
  // `data-full-bleed` avisa o `page-wrapper` que, no responsivo, esta tela
  // dispensa o recuo padrão da área de conteúdo: o painel do protótipo vai de
  // borda a borda.
  return (
    <section className={styles.screen} data-full-bleed="mobile" data-testid="hub-web-home">
      {/* Cada visão traz a sua fotografia de fundo, como no protótipo. */}
      <div
        className={[styles.panel, isVisaoGeral ? styles.panelVisaoGeral : styles.panelObras].join(
          " ",
        )}
      >
        <header className={styles.header}>
          <h1 className={styles.title}>Início</h1>

          {isNarrow ? null : (
            <div className={styles.tabs} role="tablist" aria-label="Visões da Tela Inicial">
              {visoes.map((view) => {
                const isActive = view.id === visaoAtiva;

                return (
                  <button
                    key={view.id}
                    type="button"
                    role="tab"
                    id={`${view.testId}-tab`}
                    className={[styles.tab, isActive ? styles.tabActive : ""]
                      .filter(Boolean)
                      .join(" ")}
                    aria-selected={isActive}
                    aria-controls="home-view-panel"
                    onClick={() => selecionarVisao(view.id)}
                    data-testid={view.testId}
                  >
                    {view.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* O protótipo Desktop fecha o topo do painel com o botão de fixar a
              visão; o responsivo não o traz. A ação em si depende das
              preferências do usuário, que ainda não existem no Hub, por isso
              aparece desabilitado em vez de prometer um efeito que não há. */}
          {isNarrow ? null : (
            <button
              type="button"
              className={styles.pin}
              disabled
              title="Disponível quando as preferências da Tela Inicial forem liberadas."
              aria-label="Fixar visão"
              data-testid="home-pin"
            >
              <Thumbtack className={styles.pinIcon} aria-hidden />
            </button>
          )}
        </header>

        <div
          className={[styles.viewPanel, isVisaoGeral ? styles.viewPanelOverview : ""]
            .filter(Boolean)
            .join(" ")}
          role={papelPainel}
          id="home-view-panel"
          aria-label={visaoAtivaLabel}
        >
          {status === "loading" ? (
            <StateMessage
              variant="loading"
              title="Carregando as informações da Tela Inicial..."
              testId="home-loading"
            />
          ) : null}

          {status === "error" ? (
            <StateMessage
              variant="error"
              title="Não foi possível carregar as informações da Tela Inicial."
              description={errorMessage}
              action={{ label: "Tentar novamente", onClick: tentarNovamente, icon: "refresh" }}
              testId="home-error"
            />
          ) : null}

          {status === "success" && overview && obrasEmAndamento ? (
            isVisaoGeral ? (
              <OverviewView overview={overview} />
            ) : (
              <ObrasView obrasEmAndamento={obrasEmAndamento} isNarrow={isNarrow} />
            )
          ) : null}
        </div>
      </div>
    </section>
  );
}
